<template>
  <div class="pagamento-container">
    <div class="pagamento-card">
      <div class="logo-area">
        <img src="@/assets/images/logo-branco.svg" alt="OligoCheck" class="logo" />
      </div>

      <div class="conteudo">
        <p class="label">{{ textos.label }}</p>
        <h1 class="valor">
          <small>€</small> {{ valorFormatado }}
        </h1>
        
        <div class="divisor"></div>
        
        <button 
          :class="['botao-pagar', corBotao, { 'loading': isNavigating }]" 
          @click="realizarPagamento"
          :disabled="isNavigating"
        >
          <span v-if="!isNavigating">{{ textos.botao }}</span>
          <span v-else>{{ textos.processando }}</span>
          
          <svg v-if="!isNavigating" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          <div v-else class="spinner"></div>
        </button>

        <div class="footer-seguranca">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <span>{{ textos.seguranca }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const isNavigating = ref(false)

// Traduções
const traducoes = {
  pt: {
    label: 'Valor a pagar',
    botao: 'Realizar Pagamento',
    processando: 'Processando...',
    seguranca: 'Ambiente seguro',
    redirecionando: 'Redirecionando...'
  },
  es: {
    label: 'Importe a pagar',
    botao: 'Realizar Pago',
    processando: 'Procesando...',
    seguranca: 'Entorno seguro',
    redirecionando: 'Redireccionando...'
  },
  en: {
    label: 'Amount to pay',
    botao: 'Make Payment',
    processando: 'Processing...',
    seguranca: 'Secure environment',
    redirecionando: 'Redirecting...'
  }
}

// Detecta o idioma baseado na URL (query string: ?br, ?es, ?en)
const idiomaAtual = computed(() => {
  const query = route.query
  if ('es' in query) return 'es'
  if ('en' in query) return 'en'
  if ('br' in query || 'pt' in query) return 'pt'
  // Fallback padrão se não houver parâmetro
  return 'pt'
})

// Detecta a cor do botão baseado na URL (query string: ?lar, ?az)
const corBotao = computed(() => {
  const query = route.query
  if ('az' in query) return 'azul'
  // Padrão é laranja (lar)
  return 'laranja'
})

// Retorna os textos do idioma atual
const textos = computed(() => {
  return traducoes[idiomaAtual.value]
})

// Pega o valor da URL (ex: /pagamento?valor=150.00) ou usa um padrão
const valor = computed(() => {
  const v = parseFloat(route.query.valor)
  return isNaN(v) ? 150.00 : v
})

const valorFormatado = computed(() => {
  return valor.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
})

const realizarPagamento = () => {
  if (isNavigating.value) return
  
  isNavigating.value = true
  
  console.log(`Iniciando pagamento de € ${valor.value} (Idioma: ${idiomaAtual.value}, Cor: ${corBotao.value})`)
  
  // Simula tempo de processamento antes do redirecionamento
  setTimeout(() => {
    // Aqui seria o window.location.href = 'url-do-gateway'
    console.log(textos.value.redirecionando)
  }, 1500)
}
</script>

<style scoped>
.pagamento-container {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--cor-escuro-1);
  background-image: 
    radial-gradient(circle at 50% 0%, rgba(20, 203, 240, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 100% 100%, rgba(1, 61, 126, 0.1) 0%, transparent 50%);
  padding: 20px;
  font-family: var(--regular);
}

.pagamento-card {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 32px;
  padding: 48px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 32px 64px -12px rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.8s ease-out;
}

.logo-area {
  margin-bottom: 40px;
}

.logo {
  height: 32px;
  opacity: 0.9;
}

.conteudo {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 12px;
  font-family: var(--regular);
}

.valor {
  font-family: var(--estilo);
  font-size: 56px;
  color: var(--cor-branco);
  margin: 0;
  line-height: 1;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.valor small {
  font-size: 20px;
  margin-top: 8px;
  opacity: 0.6;
  font-family: var(--regular);
}

.divisor {
  width: 60px;
  height: 4px;
  background: var(--degrade);
  border-radius: 2px;
  margin: 16px 0;
  opacity: 0.8;
}

.botao-pagar {
  width: 100%;
  color: var(--cor-branco);
  border: none;
  padding: 18px 24px;
  border-radius: 20px;
  font-family: var(--bold);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 24px;
}

.botao-pagar.loading {
  cursor: default;
  transform: none !important;
  animation: none !important;
  opacity: 0.8;
}

/* Estilos para botão Laranja */
.botao-pagar.laranja {
  background: #f58220;
  box-shadow: 0 8px 20px -4px rgba(245, 130, 32, 0.4);
  animation: pulse-laranja 2s infinite;
}

.botao-pagar.laranja:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -6px rgba(245, 130, 32, 0.6);
  animation: none;
}

/* Estilos para botão Azul */
.botao-pagar.azul {
  background: var(--cor-azul);
  box-shadow: 0 8px 20px -4px rgba(20, 203, 240, 0.4);
  animation: pulse-azul 2s infinite;
}

.botao-pagar.azul:hover {
  background: #0fbbdc;
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -6px rgba(20, 203, 240, 0.6);
  animation: none;
}

.botao-pagar svg {
  position: absolute;
  right: 24px;
}

.botao-pagar:active {
  transform: translateY(0);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  position: absolute;
  right: 24px;
}

.footer-seguranca {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse-laranja {
  0% {
    transform: scale(1);
    box-shadow: 0 8px 20px -4px rgba(245, 130, 32, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 12px 24px -4px rgba(245, 130, 32, 0.5);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 8px 20px -4px rgba(245, 130, 32, 0.4);
  }
}

@keyframes pulse-azul {
  0% {
    transform: scale(1);
    box-shadow: 0 8px 20px -4px rgba(20, 203, 240, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 12px 24px -4px rgba(20, 203, 240, 0.5);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 8px 20px -4px rgba(20, 203, 240, 0.4);
  }
}

@media (max-width: 480px) {
  .pagamento-card {
    padding: 32px 24px;
    border: none;
    background: transparent;
    box-shadow: none;
  }
  
  .valor {
    font-size: 48px;
  }
}
</style>